import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck, AlertTriangle, CheckCircle2, Clock3, CircleDot,
  ExternalLink, Search, Filter,
} from 'lucide-react';
import { CommanServices } from '@/services/commanServices';

const STATUS_META = {
  open: { label: 'Open', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: CircleDot },
  in_progress: { label: 'In Progress', color: 'bg-amber-50 text-amber-700 border-amber-300', icon: Clock3 },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
};
const PRIORITY_META = {
  low: 'bg-sky-50 text-sky-700 border-sky-200',
  medium: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  high: 'bg-rose-50 text-rose-700 border-rose-200',
};
const isOverdue = (a) => a.status !== 'completed' && a.due_date && new Date(a.due_date) < new Date();
const fmtDate = (d) => d ? new Date(d).toLocaleString([], { year: '2-digit', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const StatCard = ({ label, value, tone }) => (
  <div className={`rounded-xl border p-4 ${tone}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <p className="text-2xl font-black">{value}</p>
    <p className="text-[11px] font-bold uppercase tracking-wider opacity-70">{label}</p>
  </div>
);

const CorrectiveActionsRegister = () => {
  const [actions, setActions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (overdueOnly) params.set('overdue', 'true');
      const res = await CommanServices.listCorrectiveActions(params.toString());
      if (res?.status) setActions(res.data || []);
    } finally { setLoading(false); }
  }, [statusFilter, overdueOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter(a =>
      (a.title || '').toLowerCase().includes(q) ||
      (a.owner_name || '').toLowerCase().includes(q) ||
      (a.incident_label || '').toLowerCase().includes(q)
    );
  }, [actions, search]);

  const stats = useMemo(() => ({
    total: actions.length,
    open: actions.filter(a => a.status === 'open').length,
    in_progress: actions.filter(a => a.status === 'in_progress').length,
    completed: actions.filter(a => a.status === 'completed').length,
    overdue: actions.filter(isOverdue).length,
  }), [actions]);

  const onStatus = async (a, status) => {
    const res = await CommanServices.updateCorrectiveAction(a.id, { status });
    if (res?.status) load();
  };

  return (
    <div className="p-6 w-full min-h-screen bg-gray-50 flex flex-col" data-testid="corrective-actions-register">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center">
          <ClipboardCheck className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-900">Corrective Actions Register</h1>
          <p className="text-sm text-gray-500">Continuous improvement tracking across real incidents, exercises &amp; drills</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={stats.total} tone="bg-white border-gray-200 text-gray-900" />
        <StatCard label="Open" value={stats.open} tone="bg-slate-50 border-slate-200 text-slate-800" />
        <StatCard label="In Progress" value={stats.in_progress} tone="bg-amber-50 border-amber-200 text-amber-800" />
        <StatCard label="Completed" value={stats.completed} tone="bg-emerald-50 border-emerald-200 text-emerald-800" />
        <StatCard label="Overdue" value={stats.overdue} tone="bg-rose-50 border-rose-200 text-rose-800" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, owner, exercise…" data-testid="ca-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ca-status-filter"
          className="text-sm border border-gray-200 rounded-lg px-3 py-2">
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
        <button onClick={() => setOverdueOnly(v => !v)} data-testid="ca-overdue-toggle"
          className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border ${overdueOnly ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-700 border-gray-200'}`}>
          <AlertTriangle className="w-4 h-4" /> Overdue only
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex-1 flex flex-col">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100 text-[10px] font-bold uppercase tracking-wider text-gray-400">
          <div className="col-span-4">Action</div>
          <div className="col-span-2">Owner</div>
          <div className="col-span-2">Record</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-2">Status</div>
        </div>
        <div className="flex-1 overflow-y-auto">
        {loading && <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="p-8 text-center text-gray-400 text-sm">No corrective actions found.</div>}
        {!loading && filtered.map(a => {
          const od = isOverdue(a);
          return (
            <div key={a.id} className={`grid grid-cols-12 gap-2 px-4 py-3 border-b border-gray-50 items-center text-sm ${od ? 'bg-rose-50/40' : ''}`} data-testid={`register-action-${a.id}`}>
              <div className="col-span-4 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${PRIORITY_META[a.priority] || PRIORITY_META.medium}`}>{(a.priority || 'medium').toUpperCase()}</span>
                  <p className="font-semibold text-gray-900 truncate">{a.title}</p>
                </div>
                {a.description && <p className="text-xs text-gray-500 truncate mt-0.5">{a.description}</p>}
              </div>
              <div className="col-span-2 text-gray-700 truncate">{a.owner_name || '—'} <span className="text-[10px] text-gray-400">({a.owner_type})</span></div>
              <div className="col-span-2 min-w-0">
                <Link to={`/incident-details/${a.incident_id}`} className="text-indigo-600 hover:underline flex items-center gap-1 truncate text-xs">
                  {a.incident_label || a.incident_id} <ExternalLink className="w-3 h-3 shrink-0" />
                </Link>
              </div>
              <div className={`col-span-2 text-xs ${od ? 'text-rose-600 font-bold' : 'text-gray-600'}`}>{fmtDate(a.due_date)}</div>
              <div className="col-span-2">
                <select value={a.status} onChange={(e) => onStatus(a, e.target.value)} data-testid={`register-status-${a.id}`}
                  className={`text-[11px] font-bold border rounded-md px-2 py-1 cursor-pointer ${(STATUS_META[a.status] || STATUS_META.open).color}`}>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default CorrectiveActionsRegister;
